// ============================================================================
// SAMPLE ENTITY SERVICE (Reference Implementation)
// ============================================================================
// Business logic and API calls for Entity and Business Unit module

import { get, post, put, del } from '../client/axiosClient';
import { sampleEntityEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  ListResponse,
  EmptyResponse,
  BulkOperationResponse,
  ID,
  SampleEntity,
  CreateSampleEntityRequest,
  UpdateSampleEntityRequest,
  SampleEntityListItem,
  SampleEntityFilterParams,
  SampleEntityHierarchy,
  SampleEntityStatistics,
  SampleEntityType,
} from '../types';

/**
 * Entity Service Class
 */
class SampleEntityService {
  /**
   * Get all entities
   */
  async getAll(params?: SampleEntityFilterParams): Promise<ListResponse<SampleEntityListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(sampleEntityEndpoints.list(), params)
      : sampleEntityEndpoints.list();

    const response = await get<ListResponse<SampleEntityListItem>>(endpoint);
    return response.data;
  }

  /**
   * Get entity by ID
   */
  async getById(id: ID): Promise<ApiResponse<SampleEntity>> {
    const response = await get<ApiResponse<SampleEntity>>(sampleEntityEndpoints.getById(id));
    return response.data;
  }

  /**
   * Create new entity
   */
  async create(data: CreateSampleEntityRequest): Promise<ApiResponse<SampleEntity>> {
    const response = await post<ApiResponse<SampleEntity>>(sampleEntityEndpoints.create(), data);
    return response.data;
  }

  /**
   * Update entity
   */
  async update(id: ID, data: UpdateSampleEntityRequest): Promise<ApiResponse<SampleEntity>> {
    const response = await put<ApiResponse<SampleEntity>>(
      sampleEntityEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete entity
   */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(sampleEntityEndpoints.delete(id));
    return response.data;
  }

  /**
   * Search entities
   */
  async search(params: SampleEntityFilterParams): Promise<ListResponse<SampleEntityListItem>> {
    const response = await post<ListResponse<SampleEntityListItem>>(
      sampleEntityEndpoints.search(),
      params
    );
    return response.data;
  }

  /**
   * Get entities by type
   */
  async getByType(type: SampleEntityType): Promise<ListResponse<SampleEntityListItem>> {
    const response = await get<ListResponse<SampleEntityListItem>>(
      sampleEntityEndpoints.byType(type)
    );
    return response.data;
  }

  /**
   * Get entity hierarchy
   */
  async getHierarchy(): Promise<ApiResponse<SampleEntityHierarchy[]>> {
    const response = await get<ApiResponse<SampleEntityHierarchy[]>>(
      sampleEntityEndpoints.hierarchy()
    );
    return response.data;
  }

  /**
   * Get entity children
   */
  async getChildren(id: ID): Promise<ListResponse<SampleEntityListItem>> {
    const response = await get<ListResponse<SampleEntityListItem>>(
      sampleEntityEndpoints.children(id)
    );
    return response.data;
  }

  /**
   * Get entity statistics
   */
  async getStatistics(id: ID): Promise<ApiResponse<SampleEntityStatistics>> {
    const response = await get<ApiResponse<SampleEntityStatistics>>(
      sampleEntityEndpoints.statistics(id)
    );
    return response.data;
  }

  /**
   * Get active entities
   */
  async getActive(): Promise<ListResponse<SampleEntityListItem>> {
    const response = await get<ListResponse<SampleEntityListItem>>(
      sampleEntityEndpoints.active()
    );
    return response.data;
  }

  /**
   * Bulk delete entities
   */
  async bulkDelete(ids: ID[]): Promise<BulkOperationResponse> {
    const response = await del<BulkOperationResponse>(sampleEntityEndpoints.bulkDelete(), {
      data: { ids },
    });
    return response.data;
  }

  /**
   * Export entities
   */
  async export(filters?: SampleEntityFilterParams): Promise<Blob> {
    const endpoint = filters
      ? buildEndpointWithQuery(sampleEntityEndpoints.export(), filters)
      : sampleEntityEndpoints.export();

    const response = await get<Blob>(endpoint, {
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Import entities
   */
  async import(file: File): Promise<BulkOperationResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await post<BulkOperationResponse>(
      sampleEntityEndpoints.import(),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  /**
   * Get entities for dropdown/select
   */
  async getForDropdown(): Promise<SampleEntityListItem[]> {
    const response = await this.getActive();
    return response.data;
  }

  /**
   * Get only main entities (not Business Units)
   */
  async getEntitiesOnly(): Promise<ListResponse<SampleEntityListItem>> {
    return this.getByType('Entity');
  }

  /**
   * Get only Business Units
   */
  async getBusinessUnitsOnly(): Promise<ListResponse<SampleEntityListItem>> {
    return this.getByType('Business Unit');
  }
}

/**
 * Export singleton instance
 */
export const sampleEntityService = new SampleEntityService();
export default sampleEntityService;
